// What the scan screen says while the camera is not simply showing a picture.
// One pure function picks the state, so it can be tested and forced for screenshots.

export const CAM_STATES = ["opening", "needs_tap", "permission", "blocked", "none", "resuming", "flip"];

// Read at load: the app rewrites the address bar after redeeming the link.
const BOOT_SEARCH = typeof location !== "undefined" ? location.search : "";

/** Hidden debug: ?debug=camstate=<name> forces one state on screen. Null when absent or unknown. */
export function forcedCamState(search = BOOT_SEARCH) {
  try {
    const v = new URLSearchParams(search).get("debug") || "";
    const m = /(?:^|[,&])camstate=([a-z_]+)/.exec(v);
    return m && CAM_STATES.includes(m[1]) ? m[1] : null;
  } catch {
    return null;
  }
}

/**
 * gone: "blocked" | "none" | null. snap: "resume" | "flip" | null.
 * perm: the camera permission ("prompt" | "granted" | "denied" | ""). gumWait: getUserMedia is unanswered.
 * Returns null when the picture is showing (or a flip is crossfading: no message).
 */
export function deriveCamState({ gone = null, snap = null, camLive = false, flipping = false, needsTap = false, gumWait = false, perm = "" }) {
  if (gone) return gone;
  if (snap === "resume") return "resuming";
  if (camLive || flipping || snap === "flip") return null;
  if (gumWait && perm === "prompt") return "permission";
  if (needsTap) return "needs_tap";
  return "opening";
}

/** One short line on how to allow the camera again, in the browser's own words. */
export function blockedHelp({ browser = "", os = "", brave = false } = {}) {
  if (os === "ios") {
    return browser === "safari" ? "Tap aA in the address bar, then Website Settings, Camera, Allow."
      : "Open Settings, find this browser, and switch Camera on.";
  }
  if (brave) return "Tap the lock icon in the address bar, then Site settings, Camera, Allow.";
  if (browser === "firefox") return "Tap the lock icon in the address bar, then Permissions, Camera, Allow.";
  if (os === "android") return "Tap the lock icon in the address bar, then Permissions, Camera, Allow.";
  return "Click the lock icon in the address bar and allow the camera.";
}

/**
 * iPhone Safari remembers a camera grant only briefly unless the site is set to Allow, so
 * every Quick Scan (a fresh page) asks again. Once it has asked more than ALLOW_TIP_AFTER
 * visits in a row, say how to stop it.
 */
export const ALLOW_TIP_AFTER = 3;
export const ASKS_KEY = "cap-cam-asks";

/** Visits in a row that asked for the camera; a visit that did not ask (site set to Allow) resets it. */
export function nextAskCount(prev, asked) {
  return asked ? (Number(prev) || 0) + 1 : 0;
}

export function showAllowTip({ os = "", browser = "", asked = false, dismissed = false, asks = 0 } = {}) {
  return os === "ios" && browser === "safari" && asked && !dismissed && asks > ALLOW_TIP_AFTER;
}
